import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchSection, fetchEntries, createEntry, requestReflection } from "@/lib/api";
import { getReflectionVoice } from "@/lib/reflectionVoice";
import { notifyStopAllSpeech } from "@/lib/speechBus";
import TrustpilotAsk from "@/components/TrustpilotAsk";
import ChapterWelcome from "@/components/ChapterWelcome";
import CrisisModal from "@/components/CrisisModal";
import ReflectionThread from "@/components/ReflectionThread";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import VoiceRecorder from "@/components/VoiceRecorder";
import SectionBottomBar from "@/components/SectionBottomBar";
import ChapterTrialCountdown from "@/components/ChapterTrialCountdown";
import { useRecorder } from "@/lib/useRecorder";
import SpeakButton from "@/components/SpeakButton";
import MoodPicker from "@/components/MoodPicker";
import UserBadge from "@/components/UserBadge";
import Paywall from "@/components/Paywall";
import GiftModal from "@/components/GiftModal";
import { useEntitlements } from "@/lib/entitlements";
import { exportChapterPdf, exportChapterBadge } from "@/lib/pdf";
import { downloadSealPng, getSealShareUrl } from "@/lib/seal";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { ChevronLeft, ChevronRight, Sparkles, Check, Loader2, FileDown, Lock, Gift, Award, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function Section() {
  const { sectionId } = useParams();
  const [section, setSection] = useState(null);
  const [entries, setEntries] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [draft, setDraft] = useState("");
  const [mood, setMood] = useState(null);
  const [reflection, setReflection] = useState("");
  const [justReflected, setJustReflected] = useState(false);
  const [crisisOpen, setCrisisOpen] = useState(false);
  const [invocationPlayed, setInvocationPlayed] = useState(null); // null = unread this session
  const [reflecting, setReflecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const { canAccess, entitlements } = useEntitlements();
  const { user } = useAuth();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [sec, ents] = await Promise.all([fetchSection(sectionId), fetchEntries(sectionId)]);
        setSection(sec);
        setEntries(ents);
        setSavedIds(new Set(ents.map((e) => e.prompt_id)));
        // Auto-play the invocation only once per browser session per chapter
        const key = `umbra-invocation-played-${sectionId}`;
        if (typeof window !== "undefined") {
          const already = window.sessionStorage.getItem(key);
          setInvocationPlayed(already ? true : false);
          if (!already) {
            window.sessionStorage.setItem(key, "1");
          }
        }
      } catch (e) {
        console.error(e);
        toast.error("Could not open this chapter.");
      }
    })();
  }, [sectionId]);

  const prompt = section?.prompts?.[activeIdx];
  const existing = useMemo(
    () => entries.find((e) => e.prompt_id === prompt?.id),
    [entries, prompt]
  );

  useEffect(() => {
    // Navigating to a new prompt (or loading one with an existing entry)
    // must NOT auto-play the reflection. Only the moment the user hits
    // "Reflection" and a fresh one comes back should trigger playback.
    setDraft(existing?.answer || "");
    setReflection(existing?.reflection || "");
    setMood(existing?.mood || null);
    setJustReflected(false);
    notifyStopAllSpeech();
  }, [existing]);

  // Scroll the mobile chip switcher so the active prompt chip is visible
  // when Prev/Next / dot nav change activeIdx. `nearest` keeps the page
  // vertical scroll position untouched.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.querySelector(`[data-testid="section-prompt-switcher"] [aria-current="true"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeIdx, sectionId]);

  // One-shot celebration when the chapter is freshly completed.
  useEffect(() => {
    if (!section) return;
    const total = section.prompts.length;
    if (total === 0 || savedIds.size < total) return;
    const key = `umbra-badge-shown-${sectionId}`;
    if (typeof window !== "undefined" && !window.sessionStorage.getItem(key)) {
      window.sessionStorage.setItem(key, "1");
      toast.success("Chapter complete. Your seal is ready to print.", {
        description: "Look for the seal button up top.",
      });
    }
  }, [savedIds, section, sectionId]);

  const onTranscript = useCallback((text) => {
    setDraft((prev) => (prev ? `${prev} ${text}`.trim() : text));
  }, []);

  const recorder = useRecorder({ onTranscript });

  const handleSave = async () => {
    if (!draft.trim() || !prompt) return;
    if (locked) {
      setPaywallOpen(true);
      return;
    }
    notifyStopAllSpeech();
    setSaving(true);
    try {
      const saved = await createEntry({
        section_id: sectionId,
        prompt_id: prompt.id,
        prompt_text: prompt.text,
        answer: draft.trim(),
        reflection: reflection || null,
        mood: mood || null,
      });
      setEntries((prev) => [saved, ...prev.filter((e) => e.id !== saved.id)]);
      setSavedIds((prev) => new Set([...prev, prompt.id]));
      toast.success("Entry saved.");
    } catch (e) {
      console.error(e);
      toast.error("Could not save the entry.");
    } finally {
      setSaving(false);
    }
  };

  const handleReflect = async () => {
    if (!draft.trim() || !prompt) return;
    if (locked) {
      setPaywallOpen(true);
      return;
    }
    notifyStopAllSpeech();
    setJustReflected(false);
    setReflecting(true);
    try {
      const r = await requestReflection(prompt.text, draft.trim(), {
        sectionId,
        promptId: prompt.id,
      });
      setReflection(r.reflection);
      setJustReflected(true);
      if (r.distress?.triggered) {
        // Silence any autoplay so the resource modal isn't fighting audio.
        notifyStopAllSpeech();
        setCrisisOpen(true);
      }
      // Auto-persist the entry (with reflection) so it lands in the archive + PDF
      try {
        const saved = await createEntry({
          section_id: sectionId,
          prompt_id: prompt.id,
          prompt_text: prompt.text,
          answer: draft.trim(),
          reflection: r.reflection,
          mood: mood || null,
        });
        setEntries((prev) => [saved, ...prev.filter((e) => e.id !== saved.id)]);
        setSavedIds((prev) => new Set([...prev, prompt.id]));
      } catch (persistErr) {
        console.error("auto-persist reflection failed", persistErr);
        toast.error("Reflection couldn't be saved to your archive — try again.");
      }
    } catch (e) {
      console.error(e);
      toast.error("The mirror is quiet right now. Try again in a moment.");
    } finally {
      setReflecting(false);
    }
  };

  const handleExportPdf = () => {
    if (!section) return;
    try {
      exportChapterPdf({ section, entries });
      toast.success("Your chapter is ready.");
    } catch (e) {
      console.error(e);
      toast.error("Could not export the PDF.");
    }
  };

  const handleExportBadge = () => {
    if (!section) return;
    try {
      exportChapterBadge({
        section,
        userName: user?.name || "",
        completedAt: new Date().toISOString(),
      });
      toast.success("Your seal is ready. Print it, keep it, or share it quietly.");
    } catch (e) {
      console.error(e);
      toast.error("Could not create the badge.");
    }
  };

  const handleShareSealPng = () => {
    if (!section) return;
    try {
      downloadSealPng({
        section,
        userName: user?.name || "",
        completedAt: new Date().toISOString(),
      });
      toast.success("Your seal has been saved as a PNG.");
    } catch (e) {
      console.error(e);
      toast.error("Could not save the seal image.");
    }
  };

  const handleCopyShareLink = async () => {
    if (!section) return;
    try {
      const url = getSealShareUrl({
        section,
        userName: user?.name || "",
        completedAt: new Date().toISOString(),
      });
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — paste it anywhere.");
    } catch (e) {
      console.error(e);
      toast.error("Could not copy the share link.");
    }
  };

  if (!section) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-sans text-bone-400">Opening the chapter…</p>
      </div>
    );
  }

  const total = section.prompts.length;
  const progress = ((activeIdx + 1) / total) * 100;
  const locked = entitlements ? !canAccess(sectionId, activeIdx) : false;
  const chapterComplete = savedIds.size >= total && total > 0;
  // Chapter 2 gets a warmer "peek" overlay for trial-expired users so they
  // see exactly what they were about to unlock, not a hard "locked" wall.
  const chapter2Peek =
    locked && sectionId === "core-wounds" && Boolean(entitlements?.chapter_2_preview);

  return (
    <div className="min-h-screen pb-32 md:pb-40 relative">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-16 pt-10 sm:pt-14 md:pt-20 relative z-10">
        <div className="flex items-center justify-between mb-8 sm:mb-12">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 min-h-[44px] -mx-2 px-2 sm:min-h-0 sm:mx-0 sm:px-0 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
            data-testid={TID.backToDashboard}
          >
            <ChevronLeft className="h-3 w-3" /> The Workbook
          </Link>
          <UserBadge />
        </div>

        <ChapterTrialCountdown sectionId={sectionId} entitlements={entitlements} />

        {/* Section header */}
        <div className="mb-10 sm:mb-14 flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta mb-3 sm:mb-4">{section.subtitle}</p>
            <h1
              data-testid={TID.sectionTitle}
              className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 leading-tight mb-4 sm:mb-6"
            >
              {section.title}
            </h1>
            <p className="font-sans text-sm sm:text-base text-bone-300 leading-relaxed">{section.description}</p>
            {section.invocation && (
              <div
                className="mt-5 sm:mt-6 border-l-2 border-terracotta pl-4 sm:pl-5 py-2 flex items-start justify-between gap-3 animate-fade-up"
                data-testid={`section-invocation-${section.id}`}
              >
                <div className="flex-1">
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-2">Invocation</p>
                  <p className="font-serif italic text-bone-200 leading-relaxed">
                    {section.invocation}
                  </p>
                </div>
                <SpeakButton
                  text={section.invocation}
                  voice={getReflectionVoice(user?.preferred_voice)}
                  size="sm"
                  autoPlay={invocationPlayed === false}
                  testId={`speak-invocation-${section.id}`}
                  label="Hear the invocation"
                />
              </div>
            )}
          </div>
          {/* Actions — 2-col icon grid on mobile, right-stacked column on desktop */}
          <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-col sm:gap-2 sm:items-end mt-2 shrink-0">
            <button
              data-testid="export-pdf-btn"
              onClick={handleExportPdf}
              disabled={entries.length === 0}
              className="inline-flex items-center justify-center sm:justify-start gap-2 border border-obsidian-600 hover:border-terracotta text-bone-300 hover:text-terracotta px-3 sm:px-4 py-3 sm:py-2 font-sans text-[0.65rem] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
              title={entries.length === 0 ? "Save at least one entry to export" : "Download this chapter as a PDF"}
            >
              <FileDown className="h-4 w-4 shrink-0" /> <span className="truncate">Keepsake PDF</span>
            </button>
            {chapterComplete && (
              <button
                data-testid="chapter-badge-btn"
                onClick={handleExportBadge}
                className="inline-flex items-center justify-center sm:justify-start gap-2 border border-terracotta bg-terracotta/10 hover:bg-terracotta hover:text-obsidian-900 text-terracotta px-3 sm:px-4 py-3 sm:py-2 font-sans text-[0.65rem] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] transition-colors min-h-[44px]"
                title="Print a keepsake seal marking your completion of this chapter"
              >
                <Award className="h-4 w-4 shrink-0" /> <span className="truncate">Print your seal</span>
              </button>
            )}
            {chapterComplete && (
              <button
                data-testid="chapter-seal-share-btn"
                onClick={handleShareSealPng}
                className="inline-flex items-center justify-center sm:justify-start gap-2 border border-obsidian-600 hover:border-terracotta text-bone-300 hover:text-terracotta px-3 sm:px-4 py-3 sm:py-2 font-sans text-[0.65rem] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] transition-colors min-h-[44px]"
                title="Save the seal as a PNG image to post or share"
              >
                <Share2 className="h-4 w-4 shrink-0" /> <span className="truncate">Save seal · PNG</span>
              </button>
            )}
            {chapterComplete && (
              <button
                data-testid="chapter-seal-link-btn"
                onClick={handleCopyShareLink}
                className="inline-flex items-center justify-center sm:justify-start gap-2 border border-obsidian-600 hover:border-terracotta text-bone-300 hover:text-terracotta px-3 sm:px-4 py-3 sm:py-2 font-sans text-[0.65rem] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] transition-colors min-h-[44px]"
                title="Copy a shareable link to your seal page"
              >
                <Sparkles className="h-4 w-4 shrink-0" /> <span className="truncate">Copy share link</span>
              </button>
            )}
            {entitlements?.subscription_active && (
              <button
                data-testid="gift-chapter-btn"
                onClick={() => setGiftOpen(true)}
                className="inline-flex items-center justify-center sm:justify-start gap-2 border border-obsidian-600 hover:border-terracotta text-bone-300 hover:text-terracotta px-3 sm:px-4 py-3 sm:py-2 font-sans text-[0.65rem] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] transition-colors min-h-[44px] col-span-2 sm:col-span-1"
                title="Gift this chapter to a friend"
              >
                <Gift className="h-4 w-4 shrink-0" /> <span className="truncate">Gift this chapter</span>
              </button>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6 sm:mb-12">
          <div className="flex items-center justify-between mb-3 font-sans text-[0.65rem] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.24em] text-bone-500 gap-2">
            <span>Prompt {activeIdx + 1} of {total}</span>
            <span>{savedIds.size} of {total} spoken</span>
          </div>
          <div className="h-px bg-obsidian-600 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-terracotta transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {activeIdx === 0 && <ChapterWelcome sectionId={sectionId} />}

        {/* Mobile-only prompt-chip switcher — a horizontally scrollable row
            of numbered chips so mobile users can jump between prompts
            without hunting for the tiny dot nav at the bottom. Hidden on
            md+ because the desktop layout keeps the bottom dot-nav visible
            without additional scroll. */}
        <div
          className="md:hidden mb-8 -mx-5 sm:-mx-6 px-5 sm:px-6 overflow-x-auto no-scrollbar"
          aria-label="Prompts in this chapter"
          data-testid="section-prompt-switcher"
        >
          <div className="flex items-center gap-2 w-max pb-1">
            {section.prompts.map((p, i) => {
              const chipLocked = entitlements ? !canAccess(sectionId, i) : false;
              const isCurrent = i === activeIdx;
              const isDone = savedIds.has(p.id);
              return (
                <button
                  key={`chip-${p.id}`}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  data-testid={`prompt-chip-${p.id}`}
                  aria-current={isCurrent ? "true" : undefined}
                  aria-label={`Prompt ${i + 1}${chipLocked ? " · locked" : isDone ? " · spoken" : ""}`}
                  className={`inline-flex items-center gap-2 whitespace-nowrap min-h-[44px] px-4 py-2 font-sans text-[0.7rem] uppercase tracking-[0.22em] transition-colors ${
                    isCurrent
                      ? "border border-terracotta bg-terracotta/10 text-terracotta"
                      : chipLocked
                      ? "border border-obsidian-600 text-bone-500"
                      : isDone
                      ? "border border-bone-500/60 text-bone-200"
                      : "border border-obsidian-600 text-bone-300 hover:border-terracotta hover:text-terracotta"
                  }`}
                >
                  {chipLocked && <Lock className="h-3 w-3" aria-hidden="true" />}
                  {!chipLocked && isDone && <Check className="h-3 w-3" aria-hidden="true" />}
                  <span>Prompt {i + 1}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Prompt */}
        <div
          data-testid={TID.promptCard(prompt.id)}
          className="bg-obsidian-800 border border-obsidian-600 p-6 sm:p-8 md:p-12 animate-fade-up relative"
          key={prompt.id}
        >
          {locked && (
            <button
              type="button"
              onClick={() => setPaywallOpen(true)}
              data-testid={chapter2Peek ? "chapter-2-preview-cta" : "locked-prompt-cta"}
              className={`absolute inset-0 z-20 backdrop-blur-sm flex flex-col items-center justify-center gap-5 p-6 sm:p-8 cursor-pointer group ${
                chapter2Peek ? "bg-obsidian-900/70" : "bg-obsidian-900/85"
              }`}
              aria-label={chapter2Peek ? "See what you'd unlock" : "Unlock this prompt"}
            >
              <div className="h-12 w-12 border border-terracotta flex items-center justify-center">
                <Lock className="h-5 w-5 text-terracotta" />
              </div>
              <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta text-center">
                {chapter2Peek ? "A peek at Chapter 2" : "A gently locked prompt"}
              </p>
              <p className="font-serif text-xl sm:text-2xl md:text-3xl text-bone-100 max-w-md text-center leading-tight">
                {chapter2Peek
                  ? "This is what was unfolding when your trial ended. Answer aloud with any paid tier."
                  : "Unlock this chapter or begin a subscription to continue."}
              </p>
              <span className="font-sans text-xs uppercase tracking-[0.24em] text-bone-300 border-b border-terracotta pb-1 group-hover:text-terracotta transition-colors">
                {chapter2Peek ? "See the tiers" : "Show me the doors"}
              </span>
            </button>
          )}
          <div className="flex items-start justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
            <p
              data-testid={TID.promptText(prompt.id)}
              className="font-serif text-xl sm:text-2xl md:text-3xl text-bone-100 leading-[1.35] flex-1"
            >
              {prompt.text}
            </p>
            <SpeakButton
              text={prompt.text}
              founderClipId={user?.preferred_voice === "uncle-danny" ? prompt.id : undefined}
              testId={TID.speakPromptBtn(prompt.id)}
              label="Read prompt aloud"
            />
          </div>

          <div className="mb-8">
            <VoiceRecorder
              promptId={prompt.id}
              recording={recorder.recording}
              transcribing={recorder.transcribing}
              onStart={recorder.start}
              onStop={recorder.stop}
            />
            <p className="text-center font-sans text-xs uppercase tracking-[0.24em] text-bone-500 mt-4">
              Speak. The journal listens.
            </p>
          </div>

          <div className="mb-8">
            <label className="block font-sans uppercase tracking-[0.2em] text-[0.65rem] text-bone-500 mb-3">
              Your answer
            </label>
            <Textarea
              data-testid={TID.transcriptArea(prompt.id)}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Your spoken words will appear here. You may also edit gently."
              rows={6}
              className="bg-obsidian-900 border-obsidian-600 focus-visible:ring-1 focus-visible:ring-terracotta text-bone-100 font-serif text-lg leading-relaxed rounded-none placeholder:text-bone-500"
            />
          </div>

          <div className="mb-8">
            <MoodPicker value={mood} onChange={setMood} testIdPrefix={`mood-${prompt.id}`} />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 sm:gap-4 items-center">
            <Button
              data-testid={TID.saveEntryBtn(prompt.id)}
              onClick={handleSave}
              disabled={!draft.trim() || saving}
              className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-5 sm:px-6 py-4 sm:py-5 min-h-[48px] flex-1 sm:flex-none transition-colors"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              {savedIds.has(prompt.id) ? "Update entry" : "Save entry"}
            </Button>
            <Button
              data-testid={TID.reflectBtn(prompt.id)}
              onClick={handleReflect}
              disabled={!draft.trim() || reflecting}
              variant="outline"
              className="rounded-none border-obsidian-600 hover:border-terracotta bg-transparent text-bone-200 hover:text-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-5 sm:px-6 py-4 sm:py-5 min-h-[48px] flex-1 sm:flex-none transition-colors"
            >
              {reflecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Reflection
            </Button>
          </div>

          {reflection && (
            <ReflectionThread
              promptId={prompt.id}
              entry={existing}
              reflection={reflection}
              reflectionVoice={getReflectionVoice(user?.preferred_voice)}
              autoPlayFirst={justReflected}
              onDistress={() => setCrisisOpen(true)}
              onTurnsUpdate={(nextTurns) => {
                setEntries((prev) =>
                  prev.map((e) =>
                    e.id === existing?.id ? { ...e, turns: nextTurns } : e
                  )
                );
              }}
            />
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between mt-8 sm:mt-10 gap-2">
          <Button
            variant="ghost"
            onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
            disabled={activeIdx === 0}
            className="text-bone-300 hover:text-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs transition-colors px-2 sm:px-4 min-h-[44px]"
            data-testid="prev-prompt-btn"
          >
            <ChevronLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Previous</span>
          </Button>
          <div className="flex gap-1 sm:gap-2">
            {section.prompts.map((p, i) => {
              const dotLocked = entitlements ? !canAccess(sectionId, i) : false;
              const dotClass = (() => {
                if (i === activeIdx) return "bg-terracotta";
                if (dotLocked) return "bg-obsidian-600 ring-1 ring-terracotta/50";
                if (savedIds.has(p.id)) return "bg-bone-400";
                return "bg-obsidian-600";
              })();
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveIdx(i)}
                  className="h-11 w-6 sm:h-11 sm:w-6 flex items-center justify-center group"
                  aria-label={`Prompt ${i + 1}${dotLocked ? " (locked)" : ""}`}
                  data-testid={`prompt-dot-${p.id}`}
                >
                  <span className={`h-2 w-2 rounded-full transition-colors group-hover:scale-125 ${dotClass}`} />
                </button>
              );
            })}
          </div>
          <Button
            variant="ghost"
            onClick={() => setActiveIdx((i) => Math.min(total - 1, i + 1))}
            disabled={activeIdx === total - 1}
            className="text-bone-300 hover:text-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs transition-colors px-2 sm:px-4 min-h-[44px]"
            data-testid="next-prompt-btn"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4 sm:ml-2" />
          </Button>
        </div>
        {section && savedIds.size >= section.prompts.length && <TrustpilotAsk chapterId={sectionId} />}
      </div>

      {paywallOpen && (
        <Paywall section={section} onClose={() => setPaywallOpen(false)} />
      )}
      {giftOpen && (
        <GiftModal section={section} onClose={() => setGiftOpen(false)} />
      )}
      <CrisisModal open={crisisOpen} onDismiss={() => setCrisisOpen(false)} />
      {prompt && !locked && (
        <SectionBottomBar
          promptId={prompt.id}
          hasDraft={!!draft.trim()}
          saving={saving}
          reflecting={reflecting}
          recording={recorder.recording}
          transcribing={recorder.transcribing}
          savedAlready={savedIds.has(prompt.id)}
          onSave={handleSave}
          onReflect={handleReflect}
          onStartRecord={recorder.start}
          onStopRecord={recorder.stop}
        />
      )}
    </div>
  );
}
